import React, { FC, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import { Link, useLocation } from 'react-router-dom';
import { Menu as AntdMenu } from 'antd';
import { useIntl } from 'react-intl';

import { TestIds } from '@investown/fe/test-utils/testIds';
import {
  useHasPermissionToInviteThroughReferralResult,
  UserVerificationStatus,
  useUserDetails,
} from '@investown/fe/api-sdk';
import { isUserPremium } from '@investown/fe/ui-utils/properties';

import {
  PATH_APP,
  PATH_DASHBOARD,
  PATH_INVITE_FRIEND,
  PATH_PROPERTIES,
  PATH_USER_PROFILE,
  PATH_WALLET,
} from 'routes/routesPaths';
import MenuItem from 'components/Menu/MenuItem';
import MenuItemContainer from 'components/Menu/MenuItemContainer';
import MenuItemContent from 'components/Menu/MenuItemContent';
import PersonCircleOutlineIcon from 'components/Icons/PersonCircleOutlineIcon';
import PersonCircleIcon from 'components/Icons/PersonCircleIcon';
import WalletOutlineIcon from 'components/Icons/WalletOutlineIcon';
import WalletIcon from 'components/Icons/WalletIcon';
import { SimpleWalletBalance } from 'containers/Topbar/SimpleWalletBalance';
import AddPersonOutline from 'components/Icons/AddPersonOutline';
import AddPerson from 'components/Icons/AddPerson';
import BusinessOutlineIcon from 'components/Icons/BusinessOutlineIcon';
import BusinessIcon from 'components/Icons/BusinessIcon';
import BarChartOutlineIcon from 'components/Icons/BarChartOutlineIcon';
import BarChartIcon from 'components/Icons/BarChartIcon';
import PremiumIcon from 'components/Icons/PremiumIcon';
import PremiumIconFaded from 'components/Icons/PremiumIconFaded';
import SkeletonRow from 'components/SkeletonRow';
import UserDisplayName from 'components/UserDisplayName';
import MobileIcon from 'components/Icons/MobileIcon';
import MobileOutlineIcon from 'components/Icons/MobileOutlineIcon';

const DesktopMenu: FC = () => {
  const { pathname } = useLocation();
  const theme = useContext(ThemeContext);
  const { formatMessage } = useIntl();
  const userDetailsQueryResult = useUserDetails();
  const hasPermissionToInviteThroughReferralResult = useHasPermissionToInviteThroughReferralResult();

  const isLoading =
    userDetailsQueryResult.isLoading ||
    userDetailsQueryResult.isIdle ||
    hasPermissionToInviteThroughReferralResult.isLoading ||
    hasPermissionToInviteThroughReferralResult.isIdle;

  return (
    <MenuContainer theme={theme}>
      <RightMenu>
        <StyledAntdMenu
          // properties route has nested hierarchy so partial match of URL must be ensured
          selectedKeys={[pathname.includes(PATH_PROPERTIES) ? PATH_PROPERTIES : pathname]}
          mode="horizontal"
          forceSubMenuRender={false}
          selectable
        >
          <MenuItem key={PATH_DASHBOARD}>
            <Link to={PATH_DASHBOARD} data-testid={TestIds.Dashboard}>
              <MenuItemContainer>
                <MenuItemContent
                  icon={<BarChartOutlineIcon />}
                  activeIcon={<BarChartIcon />}
                  active={pathname === PATH_DASHBOARD}
                  content={formatMessage({ id: 'navbar.dashboard' })}
                />
              </MenuItemContainer>
            </Link>
          </MenuItem>
          <MenuItem key={PATH_PROPERTIES}>
            <Link to={PATH_PROPERTIES} data-testid={TestIds.Properties}>
              <MenuItemContainer>
                <MenuItemContent
                  icon={<BusinessOutlineIcon />}
                  activeIcon={<BusinessIcon />}
                  active={pathname.includes(PATH_PROPERTIES)}
                  content={formatMessage({ id: 'navbar.properties' })}
                />
              </MenuItemContainer>
            </Link>
          </MenuItem>
          {!isLoading &&
            userDetailsQueryResult.data?.verificationStatus === UserVerificationStatus.Verified &&
            hasPermissionToInviteThroughReferralResult.data?.hasPermissionToInviteThroughReferral === true && (
              <MenuItem key={PATH_INVITE_FRIEND}>
                <Link to={PATH_INVITE_FRIEND} data-testid={TestIds.InviteFriends}>
                  <MenuItemContainer>
                    <MenuItemContent
                      icon={<AddPersonOutline />}
                      activeIcon={<AddPerson />}
                      active={pathname === PATH_INVITE_FRIEND}
                      content={formatMessage({ id: 'navbar.inviteFriends' })}
                    />
                  </MenuItemContainer>
                </Link>
              </MenuItem>
            )}
          <MenuItem key={PATH_WALLET}>
            <Link to={PATH_WALLET} data-testid={TestIds.Wallet}>
              <MenuItemContainer>
                <MenuItemContent
                  icon={<WalletOutlineIcon />}
                  activeIcon={<WalletIcon />}
                  active={pathname === PATH_WALLET}
                  content={<SimpleWalletBalance />}
                />
              </MenuItemContainer>
            </Link>
          </MenuItem>
          <MenuItem key={PATH_APP}>
            <Link to={PATH_APP} data-testid={TestIds.App}>
              <MenuItemContainer>
                <MenuItemContent
                  icon={<MobileOutlineIcon color="transparent" />}
                  activeIcon={<MobileIcon />}
                  active={pathname === PATH_APP}
                  content={formatMessage({ id: 'navbar.app' })}
                />
              </MenuItemContainer>
            </Link>
          </MenuItem>
          <MenuItem key={PATH_USER_PROFILE}>
            <Link to={PATH_USER_PROFILE} data-testid={TestIds.UserMenuUserProfile}>
              <MenuItemContainer>
                {(userDetailsQueryResult.isLoading || userDetailsQueryResult.isIdle) && (
                  <MenuItemContent
                    icon={<SkeletonRow width={70} />}
                    activeIcon={<SkeletonRow width={70} />}
                    active={pathname === PATH_USER_PROFILE}
                    content={<SkeletonRow width={70} />}
                  />
                )}
                {userDetailsQueryResult.isError && (
                  <MenuItemContent
                    icon={<PersonCircleOutlineIcon />}
                    activeIcon={<PersonCircleIcon />}
                    active={pathname === PATH_USER_PROFILE}
                    content={<UserDisplayName firstNameOnly />}
                  />
                )}
                {userDetailsQueryResult.isSuccess && (
                  <MenuItemContent
                    icon={
                      isUserPremium(userDetailsQueryResult.data) ? (
                        <PremiumIconFaded width="25px" />
                      ) : (
                        <PersonCircleOutlineIcon />
                      )
                    }
                    activeIcon={
                      isUserPremium(userDetailsQueryResult.data) ? <PremiumIcon width="25px" /> : <PersonCircleIcon />
                    }
                    active={pathname === PATH_USER_PROFILE}
                    content={<UserDisplayName firstNameOnly />}
                  />
                )}
              </MenuItemContainer>
            </Link>
          </MenuItem>
        </StyledAntdMenu>
      </RightMenu>
    </MenuContainer>
  );
};

const RightMenu = styled.div`
  display: flex;
  justify-content: center;
  height: 100%;
  right: 0;
`;

const MenuContainer = styled.div`
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  display: flex;
  justify-content: flex-end;
`;

const StyledAntdMenu = styled(AntdMenu)`
  display: flex;
  justify-content: center;
  line-height: 1;
`;

export default DesktopMenu;
